import {
  ApiOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BarsOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  NodeIndexOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Drawer, Dropdown, Grid, Layout, Menu, Spin, Typography } from 'antd';
import { lazy, Suspense, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './auth';
import { LoginPage } from './pages/LoginPage';

const OverviewPage = lazy(() =>
  import('./pages/OverviewPage').then((module) => ({ default: module.OverviewPage })),
);
const ApplicationsPage = lazy(() =>
  import('./pages/ApplicationsPage').then((module) => ({ default: module.ApplicationsPage })),
);
const ApplicationDetailPage = lazy(() =>
  import('./pages/ApplicationDetailPage').then((module) => ({
    default: module.ApplicationDetailPage,
  })),
);
const RecordsPage = lazy(() =>
  import('./pages/RecordsPage').then((module) => ({ default: module.RecordsPage })),
);
const TracePage = lazy(() =>
  import('./pages/TracePage').then((module) => ({ default: module.TracePage })),
);
const TraceDetailPage = lazy(() =>
  import('./pages/TraceDetailPage').then((module) => ({ default: module.TraceDetailPage })),
);
const UsersPage = lazy(() =>
  import('./pages/UsersPage').then((module) => ({ default: module.UsersPage })),
);
const McpTokensPage = lazy(() =>
  import('./pages/McpTokensPage').then((module) => ({ default: module.McpTokensPage })),
);
const AuditPage = lazy(() =>
  import('./pages/AuditPage').then((module) => ({ default: module.AuditPage })),
);
const { Header, Sider, Content } = Layout;

const navigation = [
  { key: '/', icon: <DashboardOutlined />, label: '总览' },
  { key: '/applications', icon: <AppstoreOutlined />, label: '应用接入' },
  { key: '/records', icon: <DatabaseOutlined />, label: '数据查询' },
  { key: '/traces', icon: <NodeIndexOutlined />, label: 'Trace' },
  { key: '/users', icon: <TeamOutlined />, label: '内部用户' },
  { key: '/mcp-tokens', icon: <ApiOutlined />, label: 'MCP Token' },
  { key: '/audit-logs', icon: <AuditOutlined />, label: '审计记录' },
];

export function App() {
  const { user, loading, logout } = useAuth();
  if (loading) {
    return (
      <div className="app-loading">
        <Spin size="large" />
      </div>
    );
  }
  if (!user) return <LoginPage />;
  return <AdminShell onLogout={logout} />;
}

function AdminShell({ onLogout }: { onLogout: () => Promise<void> }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = screens.lg === false;
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const selected =
    navigation.find((item) => item.key !== '/' && location.pathname.startsWith(item.key))?.key ??
    '/';

  const navigateTo = (key: string) => {
    navigate(key);
    setMobileNavOpen(false);
  };

  const navigationContent = (showLabels: boolean) => (
    <>
      <div className="brand-lockup">
        <div className="brand-mark">
          <BarsOutlined />
        </div>
        {showLabels ? (
          <div>
            <strong>星豚数据平台</strong>
            <span>产品运营中心</span>
          </div>
        ) : null}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selected]}
        items={navigation}
        onClick={({ key }) => navigateTo(key)}
      />
      {showLabels ? (
        <div className="sider-status">
          <SafetyCertificateOutlined />
          <span>内部访问</span>
        </div>
      ) : null}
    </>
  );

  return (
    <Layout className="admin-shell">
      {!isMobile ? (
        <Sider
          width={224}
          collapsedWidth={72}
          collapsed={collapsed}
          trigger={null}
          className="app-sider"
        >
          {navigationContent(!collapsed)}
        </Sider>
      ) : null}
      <Drawer
        placement="left"
        width={248}
        open={mobileNavOpen}
        closable={false}
        onClose={() => setMobileNavOpen(false)}
        rootClassName="mobile-nav-drawer"
        styles={{ body: { padding: 0, background: '#17231f' } }}
      >
        <nav className="mobile-navigation" aria-label="主导航">
          {navigationContent(true)}
        </nav>
      </Drawer>
      <Layout>
        <Header className="app-header">
          <Button
            type="text"
            icon={isMobile || collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => {
              if (isMobile) setMobileNavOpen(true);
              else setCollapsed((value) => !value);
            }}
            title={isMobile ? '打开导航' : collapsed ? '展开导航' : '收起导航'}
          />
          <div className="header-spacer" />
          <Dropdown
            menu={{
              items: [
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: '退出登录',
                  onClick: () => void onLogout(),
                },
              ],
            }}
          >
            <button className="user-menu" type="button">
              <Avatar size={30}>{user?.display_name.slice(0, 1)}</Avatar>
              <span>
                <Typography.Text strong>{user?.display_name}</Typography.Text>
                <Typography.Text type="secondary">{user?.username}</Typography.Text>
              </span>
            </button>
          </Dropdown>
        </Header>
        <Content className="app-content">
          <Suspense
            fallback={
              <div className="route-loading">
                <Spin />
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<OverviewPage />} />
              <Route path="/applications" element={<ApplicationsPage />} />
              <Route path="/applications/:id" element={<ApplicationDetailPage />} />
              <Route path="/records" element={<RecordsPage />} />
              <Route path="/traces" element={<TracePage />} />
              <Route path="/traces/:traceId" element={<TraceDetailPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/mcp-tokens" element={<McpTokensPage />} />
              <Route path="/audit-logs" element={<AuditPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Content>
      </Layout>
    </Layout>
  );
}
